/**
 * Technical Performance — extended sub-views.
 *
 * Renders one of four tabs depending on `subview` prop / URL segment:
 *   - outages           — outage event list + uptime + $ impact
 *   - forecast-accuracy — AEMO POE50 vs DISPATCHLOAD bias + MAPE
 *   - degradation       — capacity-factor YoY proxy (weather-naive)
 *   - peer-benchmark    — same-region peer CF ranking
 *
 * Each panel does its own fetch on mount and gates on the portfolio's
 * asset picker. Asset is chosen from the portfolio's first asset by
 * default; UI provides an asset selector when there are several.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Card, Stack, Skeleton, Alert,
  Select, MenuItem, FormControl, Button, Collapse, IconButton,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import axios from 'axios';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

import KpiCard, { fmtAUDCompact, fmtMWhCompact } from './KpiCard';
import TechnicalWorkspaceTabs from './TechnicalWorkspaceTabs';
import StandardPeriodPicker, { usePersistedPeriod } from './PeriodPicker';
import { periodApiParams } from '../utils/periodParams';
import { useThemeColors } from '../theme/ThemeModeContext';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';


// ── Asset picker (shared) ────────────────────────────────────────────────

function useAssetPicker(portfolioId) {
  const [assets, setAssets]   = useState([]);
  const [assetId, setAssetId] = useState('');

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE_URL}/api/portfolios/${portfolioId}`)
      .then((r) => {
        if (cancelled) return;
        const arr = (r.data?.assets || []).map((a) => ({
          id: a.asset_id, name: a.name || a.asset_id,
        }));
        setAssets(arr);
        if (arr.length) setAssetId(arr[0].id);
      })
      .catch(() => { /* leave empty */ });
    return () => { cancelled = true; };
  }, [portfolioId]);

  return { assets, assetId, setAssetId };
}


function AssetPicker({ assets, assetId, onChange }) {
  // Single-asset portfolios get no picker — there's nothing to pick.
  if (!assets || assets.length < 2) return null;
  return (
    <FormControl size="small" sx={{ minWidth: 180 }}>
      <Select value={assetId} onChange={(e) => onChange(e.target.value)}>
        {assets.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
      </Select>
    </FormControl>
  );
}


// Refresh button — identical chrome to the Performance / Curtailment pages.
function RefreshButton({ onClick, loading }) {
  return (
    <IconButton
      size="small"
      onClick={onClick}
      disabled={loading}
      sx={{ color: 'text.secondary' }}
      title="Refresh"
    >
      <RefreshIcon fontSize="small" />
    </IconButton>
  );
}


// Sub-header chip — period label · date range · active asset. Identical to
// the chip under the Technical Performance / Curtailment titles so every
// tab states its window the same way.
function PeriodChip({ period, assetName }) {
  if (!period) return null;
  const fmtDate = (iso) => (iso
    ? new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—');
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5, ml: 0.25 }}>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75,
                 color: 'text.secondary', fontSize: '0.8rem' }}>
        <FiberManualRecordIcon sx={{ fontSize: 8, color: 'primary.main' }} />
        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {period.label}
        </Box>
        <Box component="span" sx={{ color: 'text.disabled' }}>·</Box>
        <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {fmtDate(period.start)}
          <Box component="span" sx={{ mx: 0.5, color: 'text.disabled' }}>→</Box>
          {fmtDate(period.end)}
        </Box>
        {assetName && (
          <>
            <Box component="span" sx={{ color: 'text.disabled' }}>·</Box>
            <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
              {assetName}
            </Box>
          </>
        )}
      </Box>
    </Stack>
  );
}


// Page header — same chrome as Technical Performance / Curtailment: title on
// the left, controls on the right. `tight` shrinks the bottom margin to the
// 1-unit gap those pages use when the PeriodChip sits directly below.
function SubviewHeader({ title, tight = false, children }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ mb: tight ? 1 : 2.5, gap: 2, flexWrap: 'wrap' }}
    >
      <Typography sx={{
        fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.025em',
        color: 'text.primary',
      }}>
        {title}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={1}>
        {children}
      </Stack>
    </Stack>
  );
}


// ── Outage digest charts ─────────────────────────────────────────────────
// Three at-a-glance reads above the event table: WHEN the outages happened
// (minutes per day across the window), WHAT KIND they were (duration mix),
// and WHICH hurt most ($ impact, top 5). Event timestamps are NEM-clock-as-
// UTC, so every label renders with timeZone:'UTC' — never browser-local.

const DURATION_BUCKETS = [
  { label: '<30m',   maxMin: 30 },
  { label: '30–60m', maxMin: 60 },
  { label: '1–4h',   maxMin: 4 * 60 },
  { label: '4–12h',  maxMin: 12 * 60 },
  { label: '>12h',   maxMin: Infinity },
];

const OUTAGE_RED = '#dc2626';

function DigestCard({ title, caption, loading, colors, children }) {
  return (
    <Card sx={{ p: 2.5, display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
          {title}
        </Typography>
        {caption && (
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            {caption}
          </Typography>
        )}
      </Stack>
      {loading
        ? <Skeleton variant="rectangular" height={190} sx={{ bgcolor: colors.skeleton, borderRadius: 1 }} />
        : children}
    </Card>
  );
}

function OutageDigest({ events, period, loading, colors }) {
  const { days, minutesByDay, bucketCounts, topEvents } = useMemo(() => {
    const out = {
      days: [], minutesByDay: [],
      bucketCounts: DURATION_BUCKETS.map(() => 0), topEvents: [],
    };
    if (!period?.start || !period?.end) return out;
    // Zero-filled day axis across the picker window so quiet stretches read
    // as quiet, not as missing.
    const d0 = new Date(period.start);
    const endMs = new Date(period.end).getTime();
    const mins = {};
    for (let t = Date.UTC(d0.getUTCFullYear(), d0.getUTCMonth(), d0.getUTCDate());
         t < endMs; t += 24 * 3600 * 1000) {
      const key = new Date(t).toISOString().slice(0, 10);
      out.days.push(key);
      mins[key] = 0;
    }
    (events || []).forEach((ev) => {
      const key = String(ev.start).slice(0, 10);
      if (key in mins) mins[key] += ev.duration_minutes || 0;
      const bi = DURATION_BUCKETS.findIndex((b) => (ev.duration_minutes || 0) < b.maxMin);
      out.bucketCounts[bi < 0 ? DURATION_BUCKETS.length - 1 : bi] += 1;
    });
    out.minutesByDay = out.days.map((k) => mins[k]);
    out.topEvents = (events || [])
      .filter((ev) => ev.has_forecast && (ev.lost_aud || 0) > 0)
      .sort((a, b) => b.lost_aud - a.lost_aud)
      .slice(0, 5);
    return out;
  }, [events, period]);

  if (!loading && !(events || []).length) return null;

  const fmtDay = (iso) => new Date(iso)
    .toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  // Long windows: label Mondays only so 90 daily ticks don't collide.
  const fmtDayTick = (v) => {
    if (days.length <= 14) return fmtDay(v);
    return new Date(v).getUTCDay() === 1 ? fmtDay(v) : '';
  };
  const fmtEventLabel = (iso) => {
    const d = new Date(iso);
    return `${d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', timeZone: 'UTC' })} `
      + d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
      {/* Full-width timeline on top — the day axis needs the room; the two
          categorical reads split the row below 50/50. */}
      <DigestCard title="Outage minutes by day" caption="when" loading={loading} colors={colors}>
        <BarChart
          height={240}
          xAxis={[{
            scaleType: 'band', data: days, valueFormatter: fmtDayTick,
            tickLabelStyle: { fontSize: 10 },
          }]}
          yAxis={[{ valueFormatter: (v) => `${Math.round(v)}m`, tickLabelStyle: { fontSize: 10 } }]}
          series={[{
            data: minutesByDay, color: OUTAGE_RED, label: 'Outage minutes',
            valueFormatter: (v, ctx) => {
              const day = days[ctx?.dataIndex] ?? '';
              return `${fmtDay(day)} · ${Math.round(v || 0)} min`;
            },
          }]}
          margin={{ left: 44, right: 8, top: 10, bottom: 22 }}
          hideLegend
        />
      </DigestCard>

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
        gap: 1.5,
      }}>
      <DigestCard title="Duration mix" caption="what kind" loading={loading} colors={colors}>
        <BarChart
          height={200}
          xAxis={[{ scaleType: 'band', data: DURATION_BUCKETS.map((b) => b.label), tickLabelStyle: { fontSize: 10 } }]}
          yAxis={[{ valueFormatter: (v) => `${Math.round(v)}`, tickLabelStyle: { fontSize: 10 } }]}
          series={[{
            data: bucketCounts, color: '#94a3b8', label: 'Events',
            valueFormatter: (v) => `${Math.round(v || 0)} event${v === 1 ? '' : 's'}`,
          }]}
          margin={{ left: 32, right: 8, top: 10, bottom: 22 }}
          hideLegend
        />
      </DigestCard>

      <DigestCard title="Costliest events" caption="top 5 · $ impact" loading={loading} colors={colors}>
        {topEvents.length ? (
          <BarChart
            layout="horizontal"
            height={200}
            yAxis={[{
              scaleType: 'band',
              data: topEvents.map((ev) => fmtEventLabel(ev.start)),
              tickLabelStyle: { fontSize: 10 },
            }]}
            xAxis={[{ valueFormatter: (v) => fmtAUDCompact(v), tickLabelStyle: { fontSize: 10 } }]}
            series={[{
              data: topEvents.map((ev) => ev.lost_aud),
              color: '#ef4444', label: '$ impact',
              valueFormatter: (v, ctx) => {
                const ev = topEvents[ctx?.dataIndex];
                return ev
                  ? `${fmtAUDCompact(v || 0)} · ${ev.duration_minutes} min · ${fmtMWhCompact(ev.lost_mwh || 0)}`
                  : fmtAUDCompact(v || 0);
              },
            }]}
            margin={{ left: 96, right: 16, top: 10, bottom: 22 }}
            hideLegend
          />
        ) : (
          <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.disabled" sx={{ fontSize: '0.85rem' }}>
              No $-quantified events (forecast required).
            </Typography>
          </Box>
        )}
      </DigestCard>
      </Box>
    </Box>
  );
}


// ── Sub-view 1: Outages ──────────────────────────────────────────────────

function OutagesView({ portfolioId, assets, assetId, setAssetId }) {
  const colors = useThemeColors();
  // Window shared with every other Technical tab (sessionStorage).
  const [period, setPeriod] = usePersistedPeriod('lumeros.technical.period', '30d');
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!assetId) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API_BASE_URL}/api/portfolios/${portfolioId}/outages`,
              { params: { asset_id: assetId, ...periodApiParams(period, { withFrequency: false }) } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [portfolioId, assetId, period, reload]);

  const totals = data?.totals || {};
  const events = data?.events || [];
  const [showEvents, setShowEvents] = useState(false);

  // Window size + downtime share — outage minutes alone don't tell you
  // whether the number is big. 1,440 min sounds alarming until you see
  // it's 1.1% of a 90-day window (129,600 min) — or genuinely alarming
  // when it's 20% of a week.
  const windowMinutes = (period?.start && period?.end)
    ? Math.max(1, Math.round((new Date(period.end) - new Date(period.start)) / 60000))
    : null;
  const downtimePct = (windowMinutes && totals.outage_minutes != null)
    ? (totals.outage_minutes / windowMinutes) * 100
    : null;

  return (
    <Box>
      <SubviewHeader title="Outages" tight>
        <AssetPicker assets={assets} assetId={assetId} onChange={setAssetId} />
        <StandardPeriodPicker value={period} onChange={setPeriod} showFrequency={false} size="small" />
        <RefreshButton onClick={() => setReload((n) => n + 1)} loading={loading} />
      </SubviewHeader>
      <PeriodChip period={period} assetName={assets?.find((a) => a.id === assetId)?.name} />
      <Box sx={{
        display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
        gap: 1.5, mb: 2,
      }}>
        <KpiCard label="Events"        loading={loading} value={totals.event_count ?? '—'} />
        <KpiCard label="Downtime"      loading={loading}
                 value={totals.outage_minutes?.toLocaleString('en-AU') ?? '—'}
                 unit={windowMinutes
                   ? `of ${windowMinutes.toLocaleString('en-AU')} min · ${downtimePct != null ? downtimePct.toFixed(2) : '—'}%`
                   : 'min'}
                 accent={downtimePct != null && downtimePct > 5 ? '#ef4444' : undefined}
                 tooltip="Outage minutes vs every minute in the selected window — the % is the share of the period the asset spent in outage." />
        <KpiCard label="Uptime"        loading={loading}
                 value={totals.uptime_pct != null ? `${totals.uptime_pct.toFixed(2)}%` : '—'} accent="#22c55e" />
        <KpiCard label="$ impact"      loading={loading}
                 value={fmtAUDCompact(totals.lost_aud || 0)} accent="#ef4444"
                 tooltip={
                   data?.pricing?.contracted_share > 0
                     ? `Lost MWh × the asset's blended rate $${data.pricing.blended_aud_per_mwh}/MWh: ${Math.round(data.pricing.contracted_share * 100)}% PPA @ $${data.pricing.strike_aud_per_mwh} + ${Math.round((1 - data.pricing.contracted_share) * 100)}% merchant @ the $${data.pricing.capture_aud_per_mwh}/MWh capture price (not the depressed midday spot).`
                     : `Lost MWh × the asset's capture price $${data?.pricing?.capture_aud_per_mwh ?? '—'}/MWh (its generation-weighted realised rate). No PPA on record, so it's all merchant.`
                 } />
      </Box>
      <OutageDigest events={events} period={period} loading={loading} colors={colors} />

      {/* Event log — collapsible reference table, same pattern as the Debt
          Metrics amortization schedule. The digest charts above carry the
          story; the row-level log is there when you need to drill in. */}
      {loading ? (
        <Card sx={{ p: 2 }}>
          <Skeleton variant="rectangular" height={160} sx={{ bgcolor: colors.skeleton }} />
        </Card>
      ) : !events.length ? (
        <Card sx={{ p: 4, textAlign: 'center', color: 'text.disabled' }}>
          <Typography>No outage events in this window. ✅</Typography>
        </Card>
      ) : (
        <Box>
          <Button
            size="small"
            onClick={() => setShowEvents((s) => !s)}
            sx={{ textTransform: 'none', fontWeight: 600, mb: 0.5 }}
          >
            {showEvents ? 'Hide' : 'Show'} event log · {events.length} event{events.length === 1 ? '' : 's'}
          </Button>
          <Collapse in={showEvents}>
      <Card sx={{ p: 0 }}>
        <Box sx={{
          display: 'grid', gap: 1, px: 2, py: 1,
          gridTemplateColumns: '2fr 2fr 1fr 1fr 1fr 1fr 1fr',
          borderBottom: '1px solid', borderColor: 'divider', bgcolor: colors.surface2,
        }}>
          {['Start', 'End', 'Duration (min)', 'Intervals', 'Lost MWh', '% of max*', '$ impact'].map((h, i) => (
            <Typography key={i} variant="caption" sx={{
              fontWeight: 600, color: 'text.secondary',
              letterSpacing: '0.06em', textTransform: 'uppercase',
              textAlign: i >= 2 ? 'right' : 'left',
            }}>{h}</Typography>
          ))}
        </Box>
        {events.map((ev, i) => (
          <Box key={i} sx={{
            display: 'grid', gap: 1, px: 2, py: 1.25,
            gridTemplateColumns: '2fr 2fr 1fr 1fr 1fr 1fr 1fr', alignItems: 'center',
            borderBottom: '1px solid', borderColor: 'divider',
            '&:last-child': { borderBottom: 'none' },
          }}>
            {/* timeZone:'UTC' — timestamps are NEM wall clock stored as UTC;
                browser-local rendering shifts them by the viewer's offset. */}
            <Typography sx={{ fontSize: '0.85rem' }}>{new Date(ev.start).toLocaleString('en-AU', { timeZone: 'UTC' })}</Typography>
            <Typography sx={{ fontSize: '0.85rem' }}>{new Date(ev.end).toLocaleString('en-AU', { timeZone: 'UTC' })}</Typography>
            <Typography sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: '0.85rem' }}>
              {ev.duration_minutes}
            </Typography>
            <Typography sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: '0.85rem' }}>
              {ev.intervals}
            </Typography>
            <Typography sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: '0.85rem' }}>
              {ev.has_forecast ? fmtMWhCompact(ev.lost_mwh) : 'n/a'}
            </Typography>
            <Typography sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: '0.85rem',
                              color: 'text.secondary' }}>
              {ev.pct_of_max != null ? `${ev.pct_of_max}%` : 'n/a'}
            </Typography>
            <Typography sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: '0.85rem',
                              color: ev.lost_aud > 0 ? '#ef4444' : 'text.disabled', fontWeight: 600 }}>
              {ev.has_forecast ? fmtAUDCompact(ev.lost_aud) : 'n/a'}
            </Typography>
          </Box>
        ))}
        <Typography variant="caption" sx={{
          display: 'block', px: 2, py: 1, color: 'text.disabled', fontStyle: 'italic',
        }}>
          *% of max — proxy until SCADA: lost MWh vs nameplate × event duration.
          {data?.pricing?.blended_aud_per_mwh != null && (
            <> · $ impact = lost MWh × ${data.pricing.blended_aud_per_mwh}/MWh
            {data.pricing.contracted_share > 0
              ? ` (${Math.round(data.pricing.contracted_share * 100)}% PPA @ $${data.pricing.strike_aud_per_mwh} + merchant @ $${data.pricing.capture_aud_per_mwh} capture).`
              : ` (capture price).`}</>
          )}
        </Typography>
      </Card>
          </Collapse>
        </Box>
      )}
    </Box>
  );
}


// ── Sub-view 2: Forecast accuracy ────────────────────────────────────────

function ForecastAccuracyView({ portfolioId, assets, assetId, setAssetId }) {
  // Window shared with every other Technical tab (sessionStorage).
  const [period, setPeriod] = usePersistedPeriod('lumeros.technical.period', '30d');
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!assetId) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API_BASE_URL}/api/portfolios/${portfolioId}/forecast-accuracy`,
              { params: {
                asset_id: assetId,
                ...periodApiParams(period, { withFrequency: false }),
                frequency: 'daily',
              } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [portfolioId, assetId, period, reload]);

  const totals = data?.totals || {};
  const series = data?.series || [];
  const labels   = series.map((s) => (s.bucket || '').slice(0, 10));
  const fc       = series.map((s) => s.forecast_mwh);
  const ac       = series.map((s) => s.actual_mwh);

  return (
    <Box>
      <SubviewHeader title="Forecast accuracy" tight>
        <AssetPicker assets={assets} assetId={assetId} onChange={setAssetId} />
        <StandardPeriodPicker value={period} onChange={setPeriod} showFrequency={false} size="small" />
        <RefreshButton onClick={() => setReload((n) => n + 1)} loading={loading} />
      </SubviewHeader>
      <PeriodChip period={period} assetName={assets?.find((a) => a.id === assetId)?.name} />
      <Box sx={{
        display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
        gap: 1.5, mb: 2,
      }}>
        <KpiCard label="Forecast" loading={loading} value={fmtMWhCompact(totals.forecast_mwh || 0)} />
        <KpiCard label="Actual"   loading={loading} value={fmtMWhCompact(totals.actual_mwh || 0)} />
        <KpiCard label="Bias"     loading={loading}
                 value={totals.bias_pct != null ? `${totals.bias_pct >= 0 ? '+' : ''}${totals.bias_pct.toFixed(1)}%` : '—'}
                 accent={totals.bias_pct >= 0 ? '#22c55e' : '#ef4444'} />
        <KpiCard label="MAPE"     loading={loading}
                 value={totals.mape_pct != null ? `${totals.mape_pct.toFixed(1)}%` : '—'} />
      </Box>
      <Card sx={{ p: 1 }}>
        <LineChart
          height={300}
          xAxis={[{ scaleType: 'point', data: labels, tickLabelStyle: { fontSize: 11 } }]}
          series={[
            { data: fc, label: 'POE50 forecast', color: '#94a3b8', showMark: false, curve: 'linear' },
            { data: ac, label: 'Dispatch actual', color: '#D98A2B', showMark: false, curve: 'linear' },
          ]}
          margin={{ left: 60, right: 24, top: 32, bottom: 36 }}
          slotProps={{ legend: { hidden: false, position: { vertical: 'top', horizontal: 'middle' } } }}
        />
      </Card>
    </Box>
  );
}


// ── Sub-view 3: Degradation proxy ────────────────────────────────────────

function DegradationView({ portfolioId, assets, assetId, setAssetId }) {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!assetId) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API_BASE_URL}/api/portfolios/${portfolioId}/degradation-proxy`,
              { params: { asset_id: assetId, years: 5 } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [portfolioId, assetId, reload]);

  const years     = data?.annual_capacity_factor || [];
  const labels    = years.map((y) => y.is_partial ? `${y.year} (ytd)` : `${y.year}`);
  const cfData    = years.map((y) => y.cf_pct);
  const avgYoy    = data?.avg_yoy_pct;
  const forecast  = data?.forecast_degradation_pct_per_year;
  const ok = avgYoy != null && avgYoy >= (forecast != null ? -forecast : -1);

  return (
    <Box>
      <SubviewHeader title="Degradation">
        <AssetPicker assets={assets} assetId={assetId} onChange={setAssetId} />
        <RefreshButton onClick={() => setReload((n) => n + 1)} loading={loading} />
      </SubviewHeader>
      <Box sx={{
        display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
        gap: 1.5, mb: 2,
      }}>
        <KpiCard label="Avg YoY change" loading={loading}
                 value={avgYoy != null ? `${avgYoy >= 0 ? '+' : ''}${avgYoy.toFixed(2)}%` : '—'}
                 accent={ok ? '#22c55e' : '#ef4444'} />
        <KpiCard label="Bank-model forecast" loading={loading}
                 value={forecast != null ? `-${forecast.toFixed(2)}%/yr` : '—'} />
        <KpiCard label="Method" loading={loading} value="CF YoY · proxy" />
      </Box>
      <Card sx={{ p: 1 }}>
        <BarChart
          height={280}
          xAxis={[{ scaleType: 'band', data: labels, tickLabelStyle: { fontSize: 11 } }]}
          yAxis={[{ valueFormatter: (v) => `${v.toFixed(0)}%`, tickLabelStyle: { fontSize: 11 } }]}
          series={[{ data: cfData, label: 'Capacity factor (%)', color: '#D98A2B',
                     valueFormatter: (v) => v == null ? '—' : `${v.toFixed(2)}%` }]}
          margin={{ left: 60, right: 24, top: 24, bottom: 32 }}
        />
        {data?.note && (
          <Typography variant="caption" sx={{
            display: 'block', px: 1.5, py: 0.75, color: 'text.disabled',
          }}>
            {data.note}
          </Typography>
        )}
      </Card>
    </Box>
  );
}


// (Peer benchmark moved to the unified Benchmarks page — see Benchmarks.js)


// ── Container ────────────────────────────────────────────────────────────

export default function TechnicalSubview({ subview }) {
  const { portfolioId } = useParams();
  const { assets, assetId, setAssetId } = useAssetPicker(portfolioId);
  const [err] = useState(null);

  let inner = null;
  if (subview === 'outages') {
    inner = <OutagesView portfolioId={portfolioId} assets={assets} assetId={assetId} setAssetId={setAssetId} />;
  } else if (subview === 'forecast-accuracy') {
    inner = <ForecastAccuracyView portfolioId={portfolioId} assets={assets} assetId={assetId} setAssetId={setAssetId} />;
  } else if (subview === 'degradation') {
    inner = <DegradationView portfolioId={portfolioId} assets={assets} assetId={assetId} setAssetId={setAssetId} />;
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
      <TechnicalWorkspaceTabs />
      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
      {inner}
    </Box>
  );
}
